// Fichier : DocumentCardHandler.ts (Version Finale - Mobile & Cache Fixed)

export {};

declare global {
  interface Window {
    customscripts?: any;
    toasterService?: any;
    offlineFileService?: any;
    // Verrou global pour éviter les écouteurs multiples
    _yoozlyCardListenerAttached?: boolean;
  }
}

// --- Constantes ---
const CARD_SELECTORS = [
  '.card-recent-documents',
  '.card-short-documents',
  '.document-file'
];
const ACTION_BUTTON_SELECTOR = 'button[data-action]';
const PREVIEW_DEBOUNCE_MS = 500;

// --- État interne ---
let lastPreviewTime = 0;
let isOpeningFile = false;

// --- Fonctions Utilitaires ---

const getNonce = (): string | null => {
  const nonceWindow = window.customscripts?.ajax_nonce;
  if (nonceWindow) return nonceWindow;
  const nonceScript = document.querySelector('script[data-nonce]');
  if (nonceScript) return nonceScript.getAttribute('data-nonce');
  const nonceMeta = document.querySelector('meta[name="wp-nonce"]');
  if (nonceMeta) return nonceMeta.getAttribute('content');
  return null;
};

const showToast = (message: string, type: 'success' | 'error'): void => {
  if (
    window.toasterService &&
    typeof window.toasterService.show === 'function'
  ) {
    window.toasterService.show(message, type, 5000);
  } else {
    console.warn(`Toaster: ${message}`);
  }
};

const fallbackCopyToClipboard = (text: string): void => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand('copy');
  } catch (err) {
    console.error('Fallback copy failed', err);
  }
  document.body.removeChild(textArea);
};

const copyToClipboard = async (text: string): Promise<void> => {
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      fallbackCopyToClipboard(text);
    }
  } else {
    fallbackCopyToClipboard(text);
  }
  showToast('Lien copié dans le presse-papiers !', 'success');
};

const generatePreviewUrl = (fileId: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const nonce = getNonce();
    if (!nonce) return reject(new Error('Nonce non trouvé'));

    const previewUrl = `${window.location.origin}/wp-admin/admin-ajax.php?action=Yoozly_front_file_preview&file_id=${fileId}&nonce=${nonce}`;
    resolve(previewUrl);
  });
};

// --- Fonctions Métier (Preview & Download) ---

const handleShareClick = async (fileId: string): Promise<void> => {
  try {
    const previewUrl = await generatePreviewUrl(fileId);
    await copyToClipboard(previewUrl);
  } catch (error) {
    showToast('Erreur lors de la génération du lien', 'error');
  }
};

/**
 * Gère l'ouverture du fichier avec le FIX MOBILE.
 * Ouvre la fenêtre AVANT les requêtes asynchrones.
 */
const handlePreviewClick = async (fileId: string): Promise<void> => {
  const now = Date.now();
  if (now - lastPreviewTime < PREVIEW_DEBOUNCE_MS) return;
  lastPreviewTime = now;

  if (isOpeningFile) return;
  isOpeningFile = true;

  // 1. OUVERTURE IMMÉDIATE (Sync)
  // Indispensable pour iOS/Android Safari/Chrome
  let targetWindow: Window | null = null;
  try {
    targetWindow = window.open('', '_blank');

    if (targetWindow) {
      // Affichage d'un loader dans la fenêtre temporaire
      targetWindow.document.write(`
        <!DOCTYPE html>
        <html lang="fr">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Chargement...</title>
          <style>
            body { font-family: sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; margin: 0; background-color: #f3f4f6; color: #374151; }
            .loader { border: 4px solid #e5e7eb; border-top: 4px solid #3b82f6; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin-bottom: 16px; }
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          </style>
        </head>
        <body>
          <div class="loader"></div>
          <p>Préparation du document...</p>
        </body>
        </html>
      `);
      targetWindow.document.close(); // Important pour arrêter le spinner du navigateur
    } else {
      console.warn('Popup bloquée');
      showToast(
        'Veuillez autoriser les pop-ups pour voir ce document',
        'error'
      );
      isOpeningFile = false;
      return;
    }
  } catch (e) {
    console.error('Erreur ouverture fenêtre', e);
    isOpeningFile = false;
    return;
  }

  try {
    let blobUrl: string | null = null;

    // 2. LOGIQUE ASYNCHRONE (Offline / Fetch)

    // A. Vérification Offline
    if (window.offlineFileService?.isFileDownloaded) {
      const isDownloaded =
        await window.offlineFileService.isFileDownloaded(fileId);
      if (isDownloaded) {
        const blob = await window.offlineFileService.getFile(fileId);
        if (blob) {
          blobUrl = window.offlineFileService.createBlobURL(blob);
        }
      }
    }

    // B. Fallback Serveur
    if (!blobUrl) {
      const previewUrl = await generatePreviewUrl(fileId);
      const response = await fetch(previewUrl, {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store'
      });

      if (!response.ok) throw new Error(`HTTP Error ${response.status}`);

      const blob = await response.blob();
      blobUrl = URL.createObjectURL(blob);
    }

    // 3. REDIRECTION (Navigation)
    if (blobUrl && targetWindow && !targetWindow.closed) {
      targetWindow.location.href = blobUrl;
      targetWindow.focus();

      // Nettoyage différé (60s)
      setTimeout(() => {
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
          window.offlineFileService?.revokeBlobURL(blobUrl);
        }
      }, 60000);
    } else {
      throw new Error('Fenêtre fermée pendant le chargement');
    }
  } catch (error) {
    console.error('[Preview] Erreur:', error);
    showToast("Erreur lors de l'ouverture", 'error');
    // Fermer la fenêtre de chargement si échec
    if (targetWindow && !targetWindow.closed) {
      targetWindow.close();
    }
  } finally {
    isOpeningFile = false;
  }
};

// --- Gestion des boutons Download ---

const updateDownloadButtonState = (
  button: HTMLElement,
  state: string
): void => {
  const isRecent = button.classList.contains('card-recent-documents-button');
  const prefix = isRecent
    ? 'card-recent-documents-button'
    : 'document-file__action';

  button.classList.remove(
    `${prefix}--downloading`,
    `${prefix}--downloaded`,
    `${prefix}--error`
  );

  if (state !== 'idle') {
    button.classList.add(`${prefix}--${state}`);
  }

  if (state === 'downloading') button.setAttribute('disabled', 'true');
  else button.removeAttribute('disabled');
};

const checkDownloadedFiles = async (): Promise<void> => {
  if (!window.offlineFileService) return;

  // Petit délai pour laisser le temps au framework JS de rendre le HTML (sécurité mobile)
  setTimeout(() => {
    const buttons = document.querySelectorAll<HTMLElement>(
      'button[data-action="offline-download"]'
    );

    buttons.forEach((button) => {
      const fileId = button.getAttribute('data-file-id');
      if (!fileId) return;

      window.offlineFileService
        .isFileDownloaded(fileId)
        .then((isDownloaded: boolean) => {
          if (isDownloaded) {
            updateDownloadButtonState(button, 'downloaded');
          }
        })
        .catch((err: any) => console.error(err));
    });
  }, 50);
};

const handleOfflineDownloadClick = async (
  button: HTMLElement,
  fileId: string,
  fileUrl: string,
  fileName: string,
  fileSize: number
): Promise<void> => {
  if (!window.offlineFileService) {
    showToast('Service hors connexion indisponible', 'error');
    return;
  }

  // Vérification suppression
  try {
    const isDownloaded =
      await window.offlineFileService.isFileDownloaded(fileId);
    if (isDownloaded) {
      if (confirm('Retirer ce document du mode hors connexion ?')) {
        await window.offlineFileService.deleteFile(fileId);
        updateDownloadButtonState(button, 'idle');
        showToast('Document retiré', 'success');
      }
      return;
    }
  } catch (e) {}

  // Téléchargement
  updateDownloadButtonState(button, 'downloading');
  showToast('Téléchargement en cours...', 'success');

  try {
    await window.offlineFileService.downloadFile(
      fileId,
      fileUrl,
      fileName,
      fileSize,
      () => {}
    );
    updateDownloadButtonState(button, 'downloaded');
    showToast('Document disponible hors connexion (30 jours).', 'success');
  } catch (error: any) {
    updateDownloadButtonState(button, 'error');
    showToast(error.message || 'Erreur téléchargement', 'error');
  }
};

// --- Gestionnaire d'événement global ---

const handleGlobalCardClick = (e: MouseEvent): void => {
  const target = e.target as HTMLElement;
  const button = target.closest(ACTION_BUTTON_SELECTOR) as HTMLElement | null;

  if (!button || !button.closest(CARD_SELECTORS.join(','))) return;

  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();

  const action = button.getAttribute('data-action');
  const fileId = button.getAttribute('data-file-id');

  if (!fileId) return;

  if (action === 'share') {
    handleShareClick(fileId);
  } else if (action === 'preview') {
    handlePreviewClick(fileId);
  } else if (action === 'offline-download') {
    const fileUrl = button.getAttribute('data-file-url');
    const fileName = button.getAttribute('data-file-name');
    const fileSize = parseInt(button.getAttribute('data-file-size') || '0', 10);

    if (fileUrl && fileName && !isNaN(fileSize)) {
      handleOfflineDownloadClick(button, fileId, fileUrl, fileName, fileSize);
    }
  }
};

// --- INITIALISATION (FIX CACHE & RACE CONDITION) ---

const startApplication = () => {
  // 1. Attacher le listener global (une seule fois)
  if (!window._yoozlyCardListenerAttached) {
    document.addEventListener('click', handleGlobalCardClick, true);
    window._yoozlyCardListenerAttached = true;
    console.log('[CardHandler] Init OK (Mobile Fix + Cache Fix)');

    // Observer pour les éléments chargés dynamiquement (AJAX/SPA)
    new MutationObserver(() => checkDownloadedFiles()).observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // 2. Vérifier les fichiers (Toujours exécuter, même si le listener est déjà là)
  // Cela force la mise à jour des icônes au rechargement
  checkDownloadedFiles();
};

const initialize = () => {
  // Si le DOM est encore en chargement (cas du cache rapide), on attend.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startApplication);
  } else {
    // Sinon (cas normal ou retardé), on lance tout de suite.
    startApplication();
  }
};

initialize();
